import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { videos } from '@/mocks/videos';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '1', 10);

  const article = useMemo(
    () => videos.find((v) => v.id === articleId) || videos[0],
    [articleId]
  );

  const relatedVideos = useMemo(
    () =>
      videos
        .filter((v) => v.category === article.category && v.id !== article.id)
        .slice(0, 4),
    [article.category, article.id]
  );

  const articleSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.jingpinvideo.com' },
          { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://www.jingpinvideo.com/category/${article.category}` },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      },
      {
        '@type': 'Article',
        headline: article.title,
        description: article.description,
        author: { '@type': 'Person', name: article.author },
        datePublished: article.date,
        publisher: { '@type': 'Organization', name: '精品视频' },
        mainEntityOfPage: { '@type': 'WebPage', '@id': `https://www.jingpinvideo.com/article/${article.id}` },
      },
      {
        '@type': 'VideoObject',
        name: article.title,
        description: article.description,
        thumbnailUrl: article.thumbnail,
        uploadDate: article.date,
        duration: article.duration,
        author: { '@type': 'Person', name: article.author },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <Navbar />
      <div className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: article.categoryName, path: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="w-full bg-background-50">
          <div className="w-full max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-10">
            <div className="relative aspect-video rounded-xl overflow-hidden mb-6 md:mb-8">
              <img
                src={article.thumbnail}
                alt={article.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent flex items-center justify-center">
                <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 text-background-50 cursor-pointer hover:bg-primary-500 transition-colors">
                  <i className="ri-play-fill text-3xl md:text-4xl ml-1"></i>
                </div>
              </div>
              <div className="absolute bottom-3 right-3 px-3 py-1.5 rounded-md bg-black/70 text-background-50 text-sm">
                <i className="ri-time-line mr-1"></i>
                {article.duration}
              </div>
              {article.isPremium && (
                <div className="absolute top-3 left-3 px-3 py-1 rounded-md bg-accent-500 text-background-50 text-sm font-medium">
                  精品内容
                </div>
              )}
            </div>

            <header className="mb-6 md:mb-8">
              <h1 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万次观看` : `${article.views}次观看`}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-star-fill text-accent-500"></i>
                  {article.rating} 分
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-4">
                <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                  {article.categoryName}
                </span>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs">
                    {tag}
                  </span>
                ))}
              </div>
            </header>

            <div className="prose prose-sm md:prose-base max-w-none">
              <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-3">
                视频简介
              </h3>
              <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
                {article.description}
              </p>
              <p className="text-foreground-600 leading-relaxed text-sm md:text-base mt-4">
                精品视频平台致力于为用户提供高质量的原创和授权内容。本视频由{article.author}精心制作，
                属于「{article.categoryName}」领域的优质内容。
                {article.isPremium
                  ? '该视频为平台精品内容，经过专业编辑团队严格筛选和审核，确保内容质量和观看价值。'
                  : '该视频内容经过平台内容审核团队审核，符合平台内容质量标准。'}
              </p>
              <p className="text-foreground-600 leading-relaxed text-sm md:text-base mt-4">
                观看本视频，您将获得专业的知识讲解和实用的技能分享。
                视频时长{article.duration}，建议在安静环境下全屏观看以获得最佳体验。
                如果您喜欢本视频，欢迎收藏并分享给更多朋友。
              </p>
            </div>

            <div className="flex items-center gap-3 mt-8 pt-6 border-t border-background-100">
              <button className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap">
                <i className="ri-thumb-up-line"></i>
                点赞
              </button>
              <button className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap">
                <i className="ri-bookmark-line"></i>
                收藏
              </button>
              <button className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap">
                <i className="ri-share-forward-line"></i>
                分享
              </button>
            </div>
          </div>
        </article>

        {relatedVideos.length > 0 && (
          <section className="w-full bg-background-100/60 py-10 md:py-14">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-6 md:mb-8">
                相关推荐
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedVideos.map((video) => (
                  <Link
                    key={video.id}
                    to={`/article/${video.id}`}
                    className="group block bg-background-50 rounded-xl border border-background-200/60 hover:border-accent-300 overflow-hidden transition-all duration-300"
                  >
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute bottom-2 right-2 px-2 py-1 rounded-md bg-black/70 text-background-50 text-xs">
                        {video.duration}
                      </div>
                    </div>
                    <div className="p-3">
                      <h4 className="font-heading text-sm font-bold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {video.title}
                      </h4>
                      <div className="flex items-center gap-3 mt-2 text-xs text-foreground-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {video.views >= 10000 ? `${(video.views / 10000).toFixed(1)}万` : video.views}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-star-fill text-accent-500"></i>
                          {video.rating}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
      <Footer />
    </>
  );
}